import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
} from "@material-tailwind/react";
import { useEffect, useState } from "react";
import { XCircleIcon } from "@heroicons/react/24/solid";
import { useDispatch, useSelector } from "react-redux";
import ScrollToTop from "@/components/ScrollToTop";
import ShowLoader from "@/components/Loader";
import Select from "react-select";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { Typography } from "@material-tailwind/react";
import { assignOrderToCRMember, fetchCRTeamMember, resetStatus } from "@/store/slices/orderSlice";
import Toaster from "@/components/Toaster";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { getRfqCommission } from "@/store/slices/rfqSlice";
import { useSearchParams } from "react-router-dom";

const customSelectStyles = {
    control: (provided, state) => ({
        ...provided,
        borderRadius: '8px',
        minHeight: '44px',
        borderColor: state.isFocused ? '#212121' : '#b0bec5',
        boxShadow: state.isFocused ? '0 0 0 1px #212121' : 'none',
        '&:hover': {
            borderColor: state.isFocused ? '#212121' : '#b0bec5',
        },
    }),
    placeholder: (provided) => ({
        ...provided,
        color: '#9e9e9e',
    }),
    menu: (provided) => ({
        ...provided,
        zIndex: 50,
        borderRadius: '8px',
    })
};

// Validation schema bounded by the manager's assigned timeslot limits via context
const schema = (t) => yup.object().shape({
    city_representative_id: yup.string().required(t('logistics_member_is_required')),
    
    time_slot_from_city_rep: yup
        .string()
        .nullable()
        .when("city_representative_id", {
            is: (value) => !!value,
            then: (schema) =>
                schema
                    .required(t('logistic_member_from_is_required'))
                    .test(
                        "from>=now",
                        t('logistic_member_from_in_past'),
                        function (v) {
                            if (!v) return true;
                            return new Date(v).getTime() >= new Date().getTime();
                        }
                    )
                    .test(
                        "from>=manager_from",
                        t('logistic_member_from_before_manager'), 
                        function (v, context) {
                            const managerFrom = context.options.context?.manager_from;
                            if (!v || !managerFrom) return true;
                            return new Date(v).getTime() >= new Date(managerFrom).getTime();
                        }
                    )
                    .test(
                        "from<=manager_to",
                        t('logistic_member_from_exceed_manager'),
                        function (v, context) {
                            const managerTo = context.options.context?.manager_to;
                            if (!v || !managerTo) return true;
                            return new Date(v).getTime() <= new Date(managerTo).getTime();
                        }
                    ),
            otherwise: (schema) => schema.notRequired(),
        }),

    time_slot_to_city_rep: yup
        .string()
        .nullable()
        .when("city_representative_id", {
            is: (value) => !!value,
            then: (schema) =>
                schema.when("time_slot_from_city_rep", {
                    is: (from) => !!from,
                    then: (s) =>
                        s.required(t('logistic_member_to_is_required'))
                            .test(
                                "to>=from",
                                t('logistic_member_to_before_from'),
                                function (v) {
                                    const from = this.parent?.time_slot_from_city_rep;
                                    if (!v || !from) return true;
                                    return new Date(v).getTime() >= new Date(from).getTime();
                                }
                            )
                            .test(
                                "to<=manager_to",
                                t('logistic_member_to_exceed_manager'),
                                function (v, context) {
                                    const managerTo = context.options.context?.manager_to;
                                    if (!v || !managerTo) return true;
                                    return new Date(v).getTime() <= new Date(managerTo).getTime();
                                }
                            ),
                    otherwise: (s) => s.notRequired(),
                }),
            otherwise: (schema) => schema.notRequired(),
        }),
});

export function AssignCRTeamMemberModal({ onClose, orderId, assigned_details }) {
    const dispatch = useDispatch();
    const [leaveConfirmation, setLeaveConfirmation] = useState(null);
    const { t } = useTranslation('order');
    const { t: ct } = useTranslation('common');
    const [searchParams] = useSearchParams();
    const rfqId = searchParams.get("rfq_id");

    // Extract Manager's details from the assigned array to enforce boundaries
    const managerDetails = Array.isArray(assigned_details) 
        ? assigned_details.find(detail => detail.designation === "Manager")
        : null;

    const managerFromDate = managerDetails?.timeslot?.from;
    const managerToDate = managerDetails?.timeslot?.to;

    const initialValue = {
        order_id: orderId,
        city_representative_id: null,
        time_slot_from_city_rep: "",
        time_slot_to_city_rep: "",
    };

    const {
        register,
        handleSubmit,
        setValue,
        getValues,
        watch,
        formState: { errors },
        reset,
    } = useForm({
        defaultValues: initialValue, 
        resolver: yupResolver(schema(t)),
        context: { manager_from: managerFromDate, manager_to: managerToDate } // Passing boundaries to validation
    });

    const { cr_team, loading } = useSelector((state) => state.order);

    const { user } = useSelector((state) => state.user);

    const { rfq_commission } = useSelector((state) => state.rfq);

    useEffect(() => {
        if (!user?.id)return;
        dispatch(fetchCRTeamMember(user?.id));
        dispatch(getRfqCommission({ rfq_id: rfqId }));
    }, [dispatch, user]);

    useEffect(() => {
        if (!user?.id || !rfqId) return;
        dispatch(fetchCRTeamMember(user?.id));
        dispatch(getRfqCommission({ rfq_id: rfqId }))
            .unwrap()
            .then((res) => {
                if (res?.city_rep_time_slot_from) {
                    setValue("time_slot_from_city_rep", res.city_rep_time_slot_from ?? "", { shouldValidate: true });
                }
                if (res?.city_rep_time_slot_to) {
                    setValue("time_slot_to_city_rep", res.city_rep_time_slot_to ?? "", { shouldValidate: true });
                }
            });
    }, [dispatch, user, rfqId]);

    const onSubmit = async (data) => {
        try {
            // Note: Update to your exact Redux action for assigning a single CR member
            const result = await dispatch(assignOrderToCRMember(data)).unwrap();
            if (result?.status) {
                Toaster.success(result.message || t('order_assigned_success'));
                dispatch(resetStatus());
                reset(initialValue);
                onClose();
            } else if (!result?.status && result?.data?.users_on_leave) {
                setLeaveConfirmation(result.data.users_on_leave);
                Toaster.error(result.message || t('assign_user_on_leave'));
                dispatch(resetStatus());
            } else {
                Toaster.error(result.message || ct('something_went_wrong'));
                dispatch(resetStatus());
            }
        } catch (err) {
            Toaster.error(err?.message || t('failed_to_assign_order'));
            dispatch(resetStatus());
        }
    };

    const handleForceAssign = async () => { 
        try {
            const payload = {
                ...getValues(),
                force_assign: true, 
            };
            const result = await dispatch(assignOrderToCRMember(payload)).unwrap();
            if (result?.status) {
                Toaster.success(result.message || t('order_assigned_success'));
                dispatch(resetStatus());
                reset(initialValue);
                onClose();
                setLeaveConfirmation(null);
            } else {
                setLeaveConfirmation(null);
                Toaster.error(result.message || ct('something_went_wrong'));
                dispatch(resetStatus());
            }
        } catch (err) {
            setLeaveConfirmation(null);
            Toaster.error(err?.message || t('failed_to_assign_order'));
            dispatch(resetStatus());
        }
    }

    // Determine min/max boundary constraints for UI inputs
    const now = new Date();
    const minDateTime = now.toISOString().slice(0, 16);
    
    let effectiveMinFromDateTime = minDateTime;
    if (managerFromDate) {
        const mFromStr = new Date(managerFromDate).toISOString().slice(0, 16);
        if (mFromStr > minDateTime) {
            effectiveMinFromDateTime = mFromStr;
        }
    }
    
    const effectiveMaxToDateTime = managerToDate ? new Date(managerToDate).toISOString().slice(0, 16) : undefined;
    
    const isMemberSelected = !!watch("city_representative_id");

    return (
        <Dialog
            open={true}
            handler={() => { setLeaveConfirmation(null); onClose(); }}
            size="md"
            className="rounded-2xl p-0"
        >
            {loading && <ShowLoader />}
            <ScrollToTop />
            <button type="button" className="sr-only" aria-hidden="true" tabIndex={0} />

            <DialogHeader className="flex justify-between items-center px-6 py-4 border-b">
                <h2 className="text-2xl font-bold text-gray-900">
                    {t('assign_logistics_team_member_title')}
                </h2>
            </DialogHeader>

            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="px-6 max-h-[65vh] overflow-y-auto pt-4 pb-6">
                    <DialogBody className="p-0">
                        <div className="space-y-6">
                            
                            {/* Member Dropdown */}
                            <div>
                                <Typography variant="small" className="mb-2 font-medium">
                                    {t('logistics_member_label')} <span className="text-red-500">*</span>
                                </Typography>
                                <Select
                                    placeholder={t('select_logistics_member_placeholder')}
                                    options={cr_team}
                                    onChange={(m) => {
                                        if (m?.value === undefined) {
                                            setValue('time_slot_from_city_rep', "");
                                            setValue('time_slot_to_city_rep', "");
                                        }
                                        setValue("city_representative_id", m ? m.value : null, { shouldValidate: true });
                                    }}
                                    styles={customSelectStyles}
                                    isClearable
                                    noOptionsMessage={() => t('no_logistics_member_available')}
                                    value={cr_team?.find(option => option.value === watch("city_representative_id")) || null}
                                />
                                {errors.city_representative_id && (
                                    <p className="text-red-500 text-sm mt-1">{errors.city_representative_id.message}</p>
                                )}
                            </div>

                            {/* Time Slots UI exactly mirroring the parent component */}
                            <div>
                                <div className="bg-gray-50 rounded-md border border-gray-200">
                                    <div className="flex flex-col md:flex-row items-center justify-between px-4 py-3 gap-3 md:gap-0">
                                        <span className="text-gray-800 text-sm font-medium whitespace-nowrap">
                                            {t('time_slot_label')} <span className="text-red-500">*</span>
                                        </span>
                                        <div className="flex items-center gap-2 w-full md:w-auto">
                                            <input
                                                type="datetime-local"
                                                disabled={!isMemberSelected}
                                                min={effectiveMinFromDateTime}
                                                max={effectiveMaxToDateTime}
                                                {...register("time_slot_from_city_rep")}
                                                className={`w-full md:w-[175px] text-center border rounded-md py-1.5 text-sm outline-none focus:ring-1 focus:ring-gray-900 ${
                                                    errors.time_slot_from_city_rep ? "border-red-500" : "border-gray-300"
                                                } ${!isMemberSelected ? "bg-gray-100 cursor-not-allowed text-gray-400" : ""}`}
                                            />
                                            <input
                                                type="datetime-local"
                                                disabled={!isMemberSelected}
                                                min={watch("time_slot_from_city_rep") || effectiveMinFromDateTime}
                                                max={effectiveMaxToDateTime}
                                                {...register("time_slot_to_city_rep")}
                                                className={`w-full md:w-[175px] text-center border rounded-md py-1.5 text-sm outline-none focus:ring-1 focus:ring-gray-900 ${
                                                    errors.time_slot_to_city_rep ? "border-red-500" : "border-gray-300"
                                                } ${!isMemberSelected ? "bg-gray-100 cursor-not-allowed text-gray-400" : ""}`}
                                            />
                                            {(watch("time_slot_from_city_rep") || watch("time_slot_to_city_rep")) && (
                                                <XCircleIcon
                                                    className="h-5 w-5 text-red-500 cursor-pointer hover:text-red-700 transition-colors flex-shrink-0"
                                                    onClick={() => {
                                                        setValue("time_slot_from_city_rep", "", { shouldValidate: true });
                                                        setValue("time_slot_to_city_rep", "", { shouldValidate: true });
                                                    }}
                                                />
                                            )}
                                        </div>
                                    </div>
                                    
                                    {/* Manager limit context helper text */}
                                    {/* {(managerFromDate || managerToDate) && (
                                        <div className="px-4 pb-2">
                                            <p className="text-xs text-gray-500 italic">
                                                * Allowed window: {moment(managerFromDate).format("DD MMM, HH:mm")} - {moment(managerToDate).format("DD MMM, HH:mm")}
                                            </p>
                                        </div>
                                    )} */}
                                </div>
                                
                                {(errors.time_slot_from_city_rep || errors.time_slot_to_city_rep) && (
                                    <p className="text-red-500 text-xs mt-2 px-1">
                                        {(errors.time_slot_from_city_rep || errors.time_slot_to_city_rep)?.message}
                                    </p>
                                )}
                            </div>
                        </div>
                    </DialogBody>
                </div>
              
                {/* Leave Confirmation Block */}
                {leaveConfirmation?.length > 0 && (
                    <div className="mx-6 mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900 shadow-sm mt-4">
                        <div className="flex items-center gap-2 mb-3">
                            <svg
                                className="h-5 w-5 text-amber-600 flex-shrink-0"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth="1.5"
                                stroke="currentColor"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                            </svg>
                            <h3 className="font-semibold text-amber-800">
                                {t('schedule_conflict_title')}
                            </h3>
                        </div>

                        <ul className="divide-y divide-amber-200/60 rounded-md bg-amber-100/50 px-3 py-1 mb-3">
                            {leaveConfirmation.map((userObj) => (
                                <li key={userObj.user_id} className="flex flex-col sm:flex-row sm:items-center justify-between py-2 text-sm gap-1 sm:gap-4">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className="font-medium text-gray-900">{userObj.name}</span>
                                        {(userObj.user_type || userObj.role) && (
                                            <span className="inline-flex items-center rounded-md bg-amber-200/70 px-1.5 py-0.5 text-xs font-medium text-amber-800">
                                                {[userObj.user_type, userObj.role].filter(Boolean).join(" • ")}
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs sm:text-sm font-medium text-amber-700 whitespace-nowrap">
                                        {moment(userObj.leave_from).format("DD MMM YYYY")} –{" "}
                                        {moment(userObj.leave_to).format("DD MMM YYYY")}
                                    </div>
                                </li>
                            ))}
                        </ul>

                        <p className="text-sm text-amber-800">
                            {t('schedule_conflict_instruction_1')} <strong className="font-semibold underline decoration-amber-500 underline-offset-2">{t('force_assign_button')}</strong> {t('schedule_conflict_instruction_2')}
                        </p>
                    </div>
                )}

                <DialogFooter className="flex justify-end gap-4 border-t pt-4">
                    <button type="button" onClick={onClose} className="px-5 py-2 rounded-lg text-gray-700 hover:bg-gray-100">
                        {t('not_now_button')}
                    </button>
                    <button type="submit" className="px-5 py-2 rounded-lg bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-medium shadow-sm">
                        {t('assign_now_button')}
                    </button>
                    {leaveConfirmation?.length > 0 && (
                        <button
                            type="button"
                            className="px-5 py-2 rounded-lg bg-red-400 hover:bg-red-500 text-white font-medium shadow-sm"
                            onClick={handleForceAssign}
                        >
                            {t('force_assign_button')}
                        </button>
                    )}
                </DialogFooter>
            </form>
        </Dialog>
    );
}

export default AssignCRTeamMemberModal;